import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { Bouton } from '../../../shared/ui/Bouton.js'
import { ChampCode } from '../../../shared/ui/ChampCode.js'
import { Bandeau } from '../../../shared/ui/Bandeau.js'
import { estErreurApi } from '../../../shared/api/erreurs.js'
import { verifierSecondFacteur } from '../api.js'
import { useOuvrirSession } from '../session.js'
import styles from './Panneau.module.css'

interface EtatNavigation {
  readonly jetonTemporaire?: string
}

/**
 * Écran B des maquettes — deuxième temps de la connexion.
 *
 * Le jeton temporaire transite par l'état de navigation, jamais par l'URL :
 * une URL part dans l'historique, dans les logs du proxy et dans les captures
 * d'écran. Il n'ouvre aucune session — il ne sert qu'à présenter un code.
 */
export function VerificationDeuxFacteurs(): React.JSX.Element {
  const naviguer = useNavigate()
  const emplacement = useLocation()
  const ouvrirSession = useOuvrirSession()
  const [code, setCode] = useState('')

  const jetonTemporaire = (emplacement.state as EtatNavigation | null)?.jetonTemporaire

  const verification = useMutation({
    mutationFn: (valeur: string) => verifierSecondFacteur(valeur, jetonTemporaire ?? ''),
    onSuccess: (resultat) => {
      // Le second facteur établit l'identité ; le poste du jour reste à
      // déclarer pour un rôle rattaché à un site (ADR 0002).
      if (resultat.statut !== 'session') {
        void naviguer('/connexion/poste', {
          replace: true,
          state: { jetonTemporaire: resultat.jetonTemporaire },
        })
        return
      }
      ouvrirSession(resultat)
      void naviguer('/', { replace: true })
    },
    onError: () => {
      setCode('')
    },
  })

  // Arrivée directe sur l'URL, ou rechargement : il n'y a pas de jeton, donc
  // rien à vérifier. On repart de la connexion plutôt que d'afficher un
  // formulaire qui échouera à coup sûr.
  if (!jetonTemporaire) return <Navigate to="/connexion" replace />

  return (
    <div className={styles.ecran}>
      <form
        className={styles.carte}
        onSubmit={(evenement) => {
          evenement.preventDefault()
          verification.mutate(code)
        }}
      >
        <div className={styles.entete}>
          <h1 className={styles.titre}>Code de vérification</h1>
          <p className={styles.sousTitre}>
            Saisissez le code à 6 chiffres affiché par votre application d’authentification.
          </p>
        </div>

        {verification.error && (
          <Bandeau
            ton="erreur"
            reference={estErreurApi(verification.error) ? verification.error.requestId : undefined}
          >
            {verification.error instanceof Error
              ? verification.error.message
              : 'Vérification impossible.'}
          </Bandeau>
        )}

        <ChampCode
          libelle="Code à six chiffres"
          valeur={code}
          onChange={setCode}
          disabled={verification.isPending}
          autoFocus
        />

        <Bouton
          type="submit"
          pleineLargeur
          enCours={verification.isPending}
          disabled={code.length < 6}
        >
          Vérifier
        </Bouton>

        <p className={styles.mention}>
          Le code change toutes les 30 secondes. En cas de perte de votre téléphone, contactez le
          Super administrateur : la réinitialisation ne peut pas se faire seule.
        </p>
      </form>
    </div>
  )
}
